import { a3Copy } from "@/components/assignments/a3-copy";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal, flushSync } from "react-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Download, Check } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { ApiError } from "@/lib/api/client";
import { checkCommit, reasonOnError } from "@/lib/api/commitSeams";
import type { Blocker, Commit, Read, Result } from "@/components/ui/box.schema";
import { assignmentsApi, type Assignment, type AssignmentDetail, type Submission, type WritingDraft, type WritingReview, type WritingSave } from "@/lib/api/assignments";
import { useAdmittedWords, useAssignmentDate, useAssignmentTime } from "./assignment-workspace-context";
import { DocumentEditor, DocumentReadOnly, type AssignmentDocument } from "./AssignmentDocument";
import { sameWriting } from "./writing-document";
import { useAssignmentDraft } from "./use-assignment-draft";
import { invalidateAssignment } from "./invalidate-assignment";
import { readWritingRecovery, writeWritingRecovery, removeWritingRecovery, eraseWritingRecovery, WRITING_RECOVERY_CLEARED, type WritingRecovery, type RecoveryDurability } from "./writing-recovery";

const errorMessage = (error: unknown) => error instanceof ApiError ? error.message : a3Copy.weCouldNotConfirmTheChangeCheckYourConnection;

function documentText(document: AssignmentDocument): string {
  return document.type === "text" ? document.text ?? "" : (document.content ?? []).map(documentText).join(["paragraph", "heading"].includes(document.type ?? "") ? "" : " ");
}

/** The box's act for this writing (board 4): its name, the sheet's name, and what waits beside it. */
export type WritingAct = { label: string; sheetTitle: string; waiting: string | null; filled: boolean };

type NativeWritingProps = {
  assignment: Assignment; expectedCycle: number;
  /** The reviewed parent binds the server cycle; Missing marks do not take a learner attempt number. */
  expectedParentSubmissionId?: string | null;
  allowed?: boolean; onSubmitted?: (receipt: Submission) => void;
  /** Where the act and its waiting line render: the Hand in box's foot. Inline when absent. */
  footTarget?: HTMLElement | null;
  act?: WritingAct;
  /** A hand-in was refused; the box reopens the brief with what changed. */
  onRefused?: () => void;
  onReviewChange?: (open: boolean) => void;
  /** A3 owns the box; the existing writer owns its private draft and recovery. */
  renderTask?: (task: WritingTaskController) => ReactNode;
  previousDocument?: AssignmentDocument | null;
  /** Why the form takes no hand-in when the work's own deadline refuses it; the closed sentence otherwise. */
  closedReason?: string | null;
};

export interface WritingTaskController {
  editor: ReactNode;
  read: Read<null>;
  draft: boolean;
  pending: boolean;
  commit: Commit;
  result: Result;
}


export function NativeWriting(props: NativeWritingProps) {
  const { me } = useAuth();
  return <RetainedWriting key={`${me?.tenant_id}:${me?.user_id}:${props.assignment.id}`} {...props} />;
}

function RetainedWriting({ allowed = true, ...props }: NativeWritingProps) {
  const { expectedCycle, expectedParentSubmissionId } = props;
  const [opened, setOpened] = useState(allowed ? { cycle: expectedCycle, parent: expectedParentSubmissionId } : null);
  useEffect(() => { if (allowed && opened === null) setOpened({ cycle: expectedCycle, parent: expectedParentSubmissionId }); }, [allowed, expectedCycle, expectedParentSubmissionId, opened]);
  if (opened === null) return null;
  return <WritingCycle {...props} expectedCycle={opened.cycle} expectedParentSubmissionId={opened.parent} frozen={!allowed || expectedCycle !== opened.cycle || expectedParentSubmissionId !== opened.parent} />;
}

function WritingCycle({ assignment, onSubmitted, expectedCycle, expectedParentSubmissionId, frozen, footTarget, act: requestedAct, onRefused, onReviewChange, renderTask, previousDocument, closedReason }: NativeWritingProps & { frozen: boolean }) {
  const act = requestedAct ?? { label: a3Copy.reviewAndHandIn, sheetTitle: a3Copy.handInAttempt(expectedCycle), waiting: null, filled: true };
  const { me } = useAuth();
  const admitted = useAdmittedWords();
  const tenant = me?.tenant_id, owner = me?.user_id;
  const date = useAssignmentDate();
  const time = useAssignmentTime();
  const client = useQueryClient();
  const [draft, setDraft] = useState<WritingDraft | null>(null);
  const [document, setDocument] = useState<AssignmentDocument | null>(null);
  const current = useRef<AssignmentDocument | null>(null);
  const base = useRef<WritingDraft | null>(null);
  const sent = useRef<WritingSave | null>(null);
  const saving = useRef<Promise<WritingDraft> | null>(null);
  const rejectedSave = useRef(false);
  const mounted = useRef(true);
  const [busy, setBusy] = useState(false);
  const [error, setErrorText] = useState<string | null>(null);
  // The save line names when IMAM last refused or missed a save.
  const [errorAt, setErrorAt] = useState<string | null>(null);
  const setError = useCallback((next: string | null) => { setErrorText(next); setErrorAt(next ? new Date().toISOString() : null); }, []);
  // A refused hand-in: the time the terms changed, or null for another cause.
  const [refusal, setRefusal] = useState<{ changedAt: string | null } | null>(null);
  const [conflict, setConflict] = useState<WritingDraft | null>(null);
  const [review, setReview] = useState<{ body: WritingReview; draft: WritingDraft; terms: AssignmentDetail } | null>(null);
  const reviewing = review !== null;
  useEffect(() => { onReviewChange?.(reviewing); }, [onReviewChange, reviewing]);
  const [submitting, setSubmitting] = useState(false);
  const [unknownSubmission, setUnknownSubmission] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [taskResult, setTaskResult] = useState<Result>({ state: "none" });
  const [readAt, setReadAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const recoverySource = useRef<WritingRecovery | null>(null);
  const [recovery, setRecovery] = useState<WritingRecovery | null>(null);
  const [durability, setDurability] = useState<RecoveryDurability | "pending">("memory");
  const durable = durability === "durable";
  const storageWrites = useRef(new Set<Promise<RecoveryDurability>>());
  const recoveryWrite = useRef<{ copy: WritingRecovery; version: number } | null>(null);
  const writeVersion = useRef(0);
  const frame = useRef<number | null>(null);
  const loadVersion = useRef(0);
  const persistenceEnabled = useRef(true);
  const copyId = useRef<string>(crypto.randomUUID());
  const erased = Boolean(draft?.erased_at);
  const readOnly = frozen || erased;
  const dirty = document !== null && JSON.stringify(document) !== JSON.stringify(draft?.document);
  useAssignmentDraft(!confirmed && (dirty || unknownSubmission || Boolean(recovery && !durable) || Boolean(sent.current)), !confirmed && (busy || submitting || unknownSubmission));
  const commitRecovery = useCallback((copy: WritingRecovery, version: number, supersedes?: WritingRecovery) => {
    const pending = writeWritingRecovery(copy, supersedes);
    storageWrites.current.add(pending);
    void pending.then(mode => {
      if (mounted.current && persistenceEnabled.current && version === writeVersion.current) setDurability(mode);
    }).finally(() => storageWrites.current.delete(pending));
  }, []);
  const flushRecovery = useCallback(() => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
    const queued = recoveryWrite.current;
    recoveryWrite.current = null;
    if (!queued || !persistenceEnabled.current) return;
    commitRecovery(queued.copy, queued.version);
  }, [commitRecovery]);
  const persist = useCallback(() => {
    const prior = base.current, writing = current.current;
    if (!tenant || !owner || !prior || !writing || !persistenceEnabled.current || !mounted.current) return;
    const version = ++writeVersion.current;
    if (!sent.current && writing === prior.document) {
      recoveryWrite.current = null;
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      frame.current = null;
      void removeWritingRecovery(tenant, owner, prior.id, copyId.current); setDurability("memory");
    } else {
      setDurability("pending");
      recoveryWrite.current = { version, copy: { copyId: copyId.current, updatedAt: Date.now(), tenant, owner, assignment: assignment.id, draft: prior.id, baseRevision: prior.revision, document: writing, pending: sent.current } };
      if (frame.current === null) frame.current = requestAnimationFrame(flushRecovery);
    }
  }, [tenant, owner, assignment.id, flushRecovery]);
  useEffect(() => {
    const cleared = () => {
      persistenceEnabled.current = false; recoveryWrite.current = null;
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      frame.current = null; setRecovery(null); setDurability("memory");
    };
    window.addEventListener(WRITING_RECOVERY_CLEARED, cleared);
    window.addEventListener("pagehide", flushRecovery);
    return () => { window.removeEventListener(WRITING_RECOVERY_CLEARED, cleared); window.removeEventListener("pagehide", flushRecovery); };
  }, [flushRecovery]);

  const load = useCallback(async (started?: WritingDraft) => {
    const version = ++loadVersion.current;
    setLoading(true); setError(null);
    try {
      const read = started ? { draft: started } : await assignmentsApi.readWriting(assignment.id);
      if (read.draft && tenant && owner) {
        if (read.draft.erased_at) await eraseWritingRecovery(tenant, owner, read.draft.id);
        else if (read.draft.submitted_id) await removeWritingRecovery(tenant, owner, read.draft.id);
      }
      const sameCycle = read.draft && (expectedParentSubmissionId === undefined
        ? read.draft.cycle === expectedCycle : read.draft.parent_submission_id === expectedParentSubmissionId);
      const next = read.draft && !read.draft.submitted_id && sameCycle ? read.draft : null;
      if (!mounted.current || version !== loadVersion.current) return;
      if (!next) {
        base.current = null; current.current = null; setDraft(null); setDocument(null); setRecovery(null); setReadAt(new Date().toISOString());
        return;
      }
      base.current = next; current.current = next.document; setDraft(next); setDocument(next.document); setReadAt(new Date().toISOString());
      const found = tenant && owner ? await readWritingRecovery(tenant, owner, assignment.id, next.id) : null;
      let recovered = found?.copy;
      recoverySource.current = recovered ?? null;
      if (recovered?.pending) {
        const ack = await assignmentsApi.confirmWritingSave(assignment.id, next.id, recovered.pending).catch(() => null);
        // Only the exact operation receipt proves acknowledgement. Equal text alone does not.
        if (ack?.acknowledged && ack.revision !== undefined) {
          if (JSON.stringify(recovered.document) === JSON.stringify(recovered.pending.document)) {
            if (tenant && owner) await removeWritingRecovery(tenant, owner, next.id, recovered.copyId, recovered);
            recovered = undefined;
          } else {
            recovered = { ...recovered, baseRevision: ack.revision, pending: null };
          }
        }
      }
      if (!mounted.current || version !== loadVersion.current || !persistenceEnabled.current) return;
      if (recovered && !next.erased_at) {
        setRecovery(recovered); setDurability(found!.durability);
      } else if (tenant && owner && next.erased_at) await eraseWritingRecovery(tenant, owner, next.id);
    } catch (cause) { if (mounted.current && version === loadVersion.current) setError(errorMessage(cause)); }
    finally { if (mounted.current && version === loadVersion.current) setLoading(false); }
  }, [assignment.id, expectedCycle, expectedParentSubmissionId, tenant, owner, setError]);
  useEffect(() => { mounted.current = true; void load(); return () => { flushRecovery(); mounted.current = false; }; }, [load, flushRecovery]);
  const starting = useRef(false);
  const startWriting = async () => {
    if (frozen || starting.current) return;
    starting.current = true; setLoading(true); setError(null);
    const version = ++loadVersion.current;
    try {
      const next = await assignmentsApi.startWriting(assignment.id);
      if (mounted.current && version === loadVersion.current) await load(next);
    } catch (cause) {
      if (mounted.current && version === loadVersion.current) { setError(errorMessage(cause)); setLoading(false); }
    } finally { starting.current = false; }
  };

  const applyErasure = useCallback(async (next: WritingDraft) => {
    if (!next.erased_at) return false;
    persistenceEnabled.current = false; recoveryWrite.current = null; writeVersion.current++;
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null; sent.current = null; current.current = next.document; base.current = next;
    setDraft(next); setDocument(next.document); setRecovery(null); setConflict(null); setError(null); setDurability("memory");
    await Promise.all([...storageWrites.current]);
    if (tenant && owner) await eraseWritingRecovery(tenant, owner, next.id);
    return true;
  }, [tenant, owner, setError]);

  const save = useCallback(async (): Promise<WritingDraft> => {
    if (saving.current) return saving.current;
    const prior = base.current;
    if (!prior || !current.current) throw new Error(a3Copy.draftUnavailable);
    if (!sent.current && JSON.stringify(prior.document) === JSON.stringify(current.current)) return prior;
    const request = sent.current ?? { operation_id: crypto.randomUUID(), expected_revision: prior.revision, document: current.current };
    sent.current = request;
    persist();
    setBusy(true); setError(null);
    const promise = assignmentsApi.saveWriting(assignment.id, prior.id, request).then(ack => {
      const next = { ...prior, ...ack, document: request.document, content_text: documentText(request.document) };
      if (mounted.current) { base.current = next; setDraft(next); sent.current = null; rejectedSave.current = false; persist(); }
      return next;
    }).catch(async (cause: unknown) => {
      if (mounted.current) {
        setError(errorMessage(cause));
        if (cause instanceof ApiError && cause.status >= 400 && cause.status < 500 && ![408, 409, 429].includes(cause.status)) {
          sent.current = null;
          rejectedSave.current = true;
          persist();
        }
        if (cause instanceof ApiError && cause.status === 409) {
          const read = await assignmentsApi.readWriting(assignment.id).catch(() => null);
          if (mounted.current && read?.draft && !(await applyErasure(read.draft))) setConflict(read.draft);
        }
      }
      throw cause;
    }).finally(() => { saving.current = null; if (mounted.current) setBusy(false); });
    saving.current = promise;
    return promise;
  }, [assignment.id, persist, applyErasure, setError]);
  useEffect(() => {
    if (readOnly || !dirty || busy || error || conflict || review || recovery) return;
    const timer = window.setTimeout(() => { void save().catch(() => undefined); }, 1000);
    return () => window.clearTimeout(timer);
  }, [dirty, busy, document, error, conflict, review, recovery, save, readOnly]);

  const openReview = async () => {
    if (readOnly || recovery) return;
    try {
      await save();
      if (JSON.stringify(base.current?.document) !== JSON.stringify(current.current)) await save();
      const [read, terms] = await Promise.all([assignmentsApi.readWriting(assignment.id), assignmentsApi.get(assignment.id)]);
      if (!mounted.current || !read.draft || await applyErasure(read.draft)) return;
      if (read.draft.content_digest !== base.current?.content_digest || JSON.stringify(current.current) !== JSON.stringify(base.current?.document)) {
        setConflict(read.draft); setError(a3Copy.theSavedWritingChangedCompareItBeforeReviewing); return;
      }
      const version = [...terms.versions].sort((a, b) => b.version_no - a.version_no)[0];
      if (!version) throw new Error(a3Copy.assignmentTermsUnavailable);
      setReview({ draft: read.draft, terms, body: { operation_id: read.draft.id, draft_id: read.draft.id, expected_revision: read.draft.revision, content_digest: read.draft.content_digest, assignment_version_id: version.id, expected_return_event_id: read.draft.review_return_event_id ?? null } });
    } catch (cause) { setError(errorMessage(cause)); }
  };
  const submit = async () => {
    if (!review || (frozen && !unknownSubmission)) return;
    setSubmitting(true); setError(null);
    try {
      const receipt = await assignmentsApi.submitWriting(assignment.id, review.body);
      if (me) await removeWritingRecovery(me.tenant_id, me.user_id, review.draft.id);
      if (!mounted.current) return;
      flushSync(() => { setConfirmed(true); setUnknownSubmission(false); setReview(null); setRefusal(null); });
      client.setQueryData<Submission[]>(["assignment", assignment.id, "submissions"], old => [...(old ?? []).filter(item => item.id !== receipt.id), receipt]);
      onSubmitted?.(receipt);
      void invalidateAssignment(client, assignment.course_id, assignment.id);
    } catch (cause) {
      if (mounted.current) {
        setError(errorMessage(cause));
        const refused = cause instanceof ApiError && cause.status >= 400 && cause.status < 500;
        setUnknownSubmission(!refused);
        if (refused) {
          const terms = await assignmentsApi.get(assignment.id).catch(() => null);
          const newest = terms ? [...terms.versions].sort((a, b) => b.version_no - a.version_no)[0] : undefined;
          if (mounted.current) setRefusal({ changedAt: newest && newest.id !== review.body.assignment_version_id ? newest.created_at : null });
          void client.invalidateQueries({ queryKey: ["assignment", assignment.id, "detail"] });
        }
      }
    } finally { if (mounted.current) setSubmitting(false); }
  };
  const exportWriting = () => {
    const blob = new Blob([documentText(current.current ?? {})], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = window.document.createElement("a"); anchor.href = url; anchor.download = "my-assignment-draft.txt"; anchor.click(); URL.revokeObjectURL(url);
  };
  const task = (editor: ReactNode, read: Read<null>): WritingTaskController => {
    const keep = (result: Result) => { if (mounted.current) setTaskResult(result); return result; };
    const acknowledge = async (receipt: Submission): Promise<Result> => {
      if (tenant && owner && base.current) await removeWritingRecovery(tenant, owner, base.current.id);
      if (mounted.current) {
        flushSync(() => { setConfirmed(true); setUnknownSubmission(false); });
        client.setQueryData<Submission[]>(["assignment", assignment.id, "submissions"], old => [...(old ?? []).filter(item => item.id !== receipt.id), receipt]);
        onSubmitted?.(receipt);
        void invalidateAssignment(client, assignment.course_id, assignment.id);
      }
      return keep({ state: "confirmed", at: receipt.submitted_at,
        effects: [a3Copy.handedInAttempt(receipt.attempt_number), admitted(receipt)], remains: [a3Copy.yourInstructorCanNowReadThisAttempt] });
    };
    const unknown = (key: string): Result => ({
      state: "unknown", requestId: key, cap: a3Copy.handInNotConfirmed,
      sentence: a3Copy.yourWorkMayHaveArrivedCheckThisHandIn,
      check: { verb: a3Copy.checkHandIn, note: a3Copy.thisReadsTheSameHandInItNeverSends, run: async () => {
        try {
          const checked = await checkCommit<{ state: string; submission_id: string }>(key);
          if (checked.state === "confirmed") {
            if (typeof checked.receipt.result.submission_id !== "string") return keep(unknown(key));
            return acknowledge(await assignmentsApi.getSubmission(checked.receipt.result.submission_id));
          }
          if (checked.state === "not_committed") {
            setUnknownSubmission(false);
            return keep({ state: "refused", cap: a3Copy.notHandedIn, sentence: a3Copy.noHandInWasRecordedYourWritingIsKept, draft: "kept" });
          }
        } catch { /* An unavailable Check cannot prove the write did not land. */ }
        return keep(unknown(key));
      } },
    });
    const blockers = (): Blocker[] => {
      if (readOnly) return [{ kind: "missing", part: "writing", sentence: closedReason ?? a3Copy.thisWorkIsClosedToNewHandInsYour }];
      if (conflict) return [{ kind: "conflict", part: "writing", sentence: a3Copy.chooseWhichWritingToKeepBeforeHandingItIn }];
      if (recovery) return [{ kind: "missing", part: "writing", sentence: a3Copy.chooseWhetherToUseYourRecoveredWritingFirst }];
      if (error) return [{ kind: "missing", part: "writing", sentence: a3Copy.yourWritingHasNotBeenSavedResolveItsSave }];
      if (busy || dirty || loading) return [{ kind: "pending" }];
      if (!current.current || !documentText(current.current).trim()) return [{ kind: "missing", part: "writing", sentence: a3Copy.writeYourResponseFirst }];
      if (previousDocument && sameWriting(current.current, previousDocument)) return [{ kind: "unchanged", part: "writing", attempt: expectedCycle - 1 }];
      return [];
    };
    return {
      editor, read, draft: !confirmed && Boolean(draft && !draft.submitted_id), pending: submitting, result: taskResult,
      commit: {
        verb: a3Copy.handInAttempt(expectedCycle), cap: a3Copy.whenYouHandIn,
        effects: () => [{ id: "hand-in", sentence: a3Copy.sendTheWritingShownHereToYourInstructorAs }],
        blockers,
        run: async key => {
          const blocked = blockers();
          if (blocked.length) return keep({ state: "refused", cap: a3Copy.notHandedIn, sentence: a3Copy.resolveTheWritingSBlockerBeforeHandingItIn, draft: "kept" });
          setSubmitting(true);
          try {
            const [read, terms] = await Promise.all([assignmentsApi.readWriting(assignment.id), assignmentsApi.get(assignment.id)]);
            if (!read.draft || await applyErasure(read.draft)) throw new ApiError(409, a3Copy.yourSavedWritingIsNoLongerAvailable);
            const expectedTerms = "versions" in assignment ? (assignment as AssignmentDetail).versions : [];
            const expectedVersion = [...expectedTerms].sort((a, b) => b.version_no - a.version_no)[0]?.id;
            const version = [...terms.versions].sort((a, b) => b.version_no - a.version_no)[0];
            if (!version || (expectedVersion && expectedVersion !== version.id)) {
              onRefused?.();
              return keep({ state: "refused", cap: a3Copy.notHandedIn, sentence: a3Copy.theTermsChangedReadTheCurrentBriefBeforeHanding, draft: "kept" });
            }
            if (read.draft.content_digest !== base.current?.content_digest || !sameWriting(read.draft.document, current.current)) {
              setConflict(read.draft);
              return keep({ state: "refused", cap: a3Copy.notHandedIn, sentence: a3Copy.theSavedWritingChangedCompareTheCopiesYourWriting, draft: "kept" });
            }
            const body: WritingReview = { operation_id: read.draft.id, draft_id: read.draft.id,
              expected_revision: read.draft.revision, content_digest: read.draft.content_digest,
              assignment_version_id: version.id, expected_return_event_id: read.draft.review_return_event_id ?? null };
            return await acknowledge(await assignmentsApi.submitWriting(assignment.id, body, key));
          } catch (cause) {
            if (cause instanceof ApiError && cause.status >= 400 && cause.status < 500) {
              onRefused?.();
              return keep({ state: "refused", cap: a3Copy.notHandedIn, sentence: reasonOnError(cause)?.message ?? errorMessage(cause), draft: "kept" });
            }
            setUnknownSubmission(true);
            return keep(unknown(key));
          } finally { if (mounted.current) setSubmitting(false); }
        },
      },
    };
  };
  if (loading) {
    const content = <div className="grid gap-3" aria-busy="true"><p role="status" className="text-meta text-muted-foreground">{a3Copy.openingYourPrivateWriting}</p><Skeleton className="min-h-[48vh]" /></div>;
    return renderTask ? renderTask(task(content, { status: "loading" })) : content;
  }
  if (!draft || !document) {
    if (!error) {
      const content = <div className="grid gap-3"><p>{a3Copy.startPrivateWriting}</p><Button variant="outline" className="justify-self-start" disabled={frozen} onClick={() => void startWriting()}>{a3Copy.startWriting}</Button></div>;
      return renderTask ? renderTask(task(content, { status: "ready", value: null, readAt: readAt! })) : content;
    }
    const content = <div role="alert" className="grid gap-3 py-6 text-ui"><p>{error ?? a3Copy.writingIsUnavailable}</p><div><Button variant="outline" onClick={() => void load()}>{a3Copy.retryWriting}</Button></div></div>;
    return renderTask ? renderTask(task(content, { status: "failed", sentence: error ?? a3Copy.writingIsUnavailable, retry: () => void load() })) : content;
  }
  const words = documentText(document).trim().split(/\s+/u).filter(Boolean).length;
  const saveFailed = Boolean(error) && !conflict && !frozen && !recovery && !review;
  const reviewable = !(readOnly || busy || Boolean(error) || Boolean(recovery) || !documentText(document).trim() || confirmed);
  const signal = act.filled && !review;
  const waiting = [act.waiting, saveFailed && a3Copy.yourWritingIsNotSavedToIMAM].filter(Boolean).join(" · ");
  // The review binds the saved draft (ADR 0002), so a failed save holds the hand-in and Save current writing ranks.
  const foot = confirmed || readOnly ? null : <div className="flex flex-wrap items-center justify-between gap-3">
    {waiting && <p data-task-waiting className={`flex items-center gap-2 text-meta ${signal ? "text-status-warning" : "text-muted-foreground"}`}>{signal && <span aria-hidden className="size-[7px] shrink-0 rounded-full bg-current" />}{waiting}</p>}
    <div className="ms-auto flex flex-wrap justify-end gap-2">
      {saveFailed && <Button variant={act.filled ? "default" : "outline"} data-session-filled-action={act.filled || undefined} disabled={busy} onClick={() => void save().catch(() => undefined)}>{a3Copy.saveCurrentWriting}</Button>}
      <Button variant={signal && !saveFailed ? "default" : "outline"} data-session-filled-action={(signal && !saveFailed) || undefined} onClick={() => void openReview()} disabled={!reviewable}>{act.label}</Button>
    </div>
  </div>;
  const closeReview = () => { setReview(null); setError(null); setRefusal(null); };
  const content = <section className="grid min-w-0 gap-3 [&_[role=textbox]]:min-h-56" aria-label={a3Copy.privateWriting}>
    <div className="grid min-w-0 gap-0.5 text-meta text-muted-foreground">
      <p role="status" aria-live="polite">{erased ? a3Copy.privateDraftErased : recovery ? a3Copy.recoveredWritingIsReadyToCompare : busy ? durable ? a3Copy.savingToIMAMSavedInThisBrowser : a3Copy.savingToIMAMSavingRecoveryInThisBrowser : error ? a3Copy.notSavedToIMAM(errorAt ? ` at ${time(errorAt)}` : "", durable ? a3Copy.savedInThisBrowserYourChangesRemainInThis : durability === "fallback" ? a3Copy.aTemporaryBrowserCopyMayBeLostIfThe : a3Copy.yourChangesRemainInThisPage) : dirty ? durable ? a3Copy.savedInThisBrowserWaitingToSaveToIMAM : durability === "pending" ? a3Copy.savingRecoveryInThisBrowser : durability === "fallback" ? a3Copy.temporaryBrowserCopyCrashRecoveryIsUnavailable : a3Copy.unsavedChangesInThisPage : <><Check className="me-1 inline size-3.5" strokeWidth={1.75} aria-hidden="true" />{a3Copy.savedToIMAM}<time dateTime={draft.saved_at} title={date(draft.saved_at)}>{time(draft.saved_at)}</time>{a3Copy.privateToYouUntilYouHandItIn}</>}</p>
      <p>{error || busy || dirty || recovery || erased ? a3Copy.privateToYouUntilYouHandItIn2 : ""}{words} {words === 1 ? "word" : "words"}</p>
    </div>
    {erased ? <p role="alert" className="text-ui">{a3Copy.thisPrivateDraftWasErasedWhenItsCourseWas}</p> : frozen && <div role="alert" className="grid gap-3 border-t border-rule pt-4 text-ui"><p>{a3Copy.thisWorkNoLongerTakesHandInsDownloadYour}</p><div><Button variant="outline" onClick={exportWriting}><Download className="size-4" strokeWidth={1.75} aria-hidden="true" />{a3Copy.downloadYourWriting}</Button></div></div>}
    {recovery && <section aria-label={a3Copy.recoveredWriting} className="grid gap-3 border-t border-rule pt-4"><h2 className="text-body font-semibold text-foreground">{a3Copy.writingRecoveredFromThisBrowser}</h2><p className="text-ui">{a3Copy.thisWritingWasNotConfirmedSavedToIMAMRead}</p>{durability === "fallback" && <p role="alert" className="text-ui">{a3Copy.thisTemporaryBrowserCopyIsAvailableNowButMay}</p>}<DocumentReadOnly document={recovery.document} /><div className="flex flex-wrap gap-2"><Button variant="outline" onClick={() => { if (me) removeWritingRecovery(me.tenant_id, me.user_id, draft.id, recovery.copyId, recoverySource.current ?? recovery); setRecovery(null); setDurability("memory"); }}>{a3Copy.discardRecoveredWriting}</Button><Button variant="outline" onClick={() => { current.current = recovery.document; base.current = { ...draft, revision: recovery.baseRevision }; setDocument(recovery.document); const version = ++writeVersion.current; setDurability("pending"); commitRecovery({ ...recovery, copyId: copyId.current }, version, recoverySource.current ?? recovery); if (draft.revision !== recovery.baseRevision) { sent.current = null; setConflict(draft); setError(a3Copy.theServerHasADifferentRevisionChooseWhichWriting); } else { sent.current = recovery.pending; } setRecovery(null); }}>{a3Copy.useRecoveredWriting}</Button></div></section>}
    {error && !review && <Alert variant="destructive"><AlertDescription className="gap-3"><p>{error}</p><p className="text-ui text-foreground">{durable ? a3Copy.yourUnsavedWritingIsKeptInThisBrowserFor : durability === "fallback" ? a3Copy.keepThisPageOpenOrDownloadYourWritingThe : a3Copy.keepThisPageOpenOrDownloadYourWritingBrowser}</p><div className="flex flex-wrap gap-2 text-foreground"><Button variant="outline" onClick={exportWriting}><Download className="size-4" strokeWidth={1.75} aria-hidden="true" />{a3Copy.downloadYourWriting}</Button></div></AlertDescription></Alert>}
    {conflict && <section aria-label={a3Copy.resolveWritingConflict} className="grid gap-3 border-t border-rule pt-4"><h2 className="text-body font-semibold text-foreground">{a3Copy.aNewerVersionWasSavedElsewhere}</h2><p className="text-ui">{a3Copy.yourWritingIsStillInTheEditorCompareIt}{date(conflict.saved_at)}{a3Copy.beforeChoosingWhichToKeep}</p><details><summary className="cursor-pointer text-ui font-medium">{a3Copy.readSavedVersion}</summary><DocumentReadOnly document={conflict.document} /></details><div className="flex flex-wrap gap-2"><Button variant="outline" onClick={() => { current.current = conflict.document; base.current = conflict; setDocument(conflict.document); setDraft(conflict); sent.current = null; persist(); setConflict(null); setError(null); }}>{a3Copy.useSavedVersion}</Button><Button variant="outline" disabled={frozen || Boolean(conflict.submitted_id)} onClick={() => { base.current = conflict; setDraft(conflict); sent.current = null; persist(); setConflict(null); setError(null); void save().catch(() => undefined); }}>{a3Copy.keepMyWritingAndSave}</Button></div></section>}
    <DocumentEditor value={document} editable={!readOnly && !review && !confirmed && !recovery && !submitting && !unknownSubmission} onChange={next => { if (readOnly || review || confirmed || recovery || submitting || unknownSubmission || !persistenceEnabled.current) return; current.current = next; setDocument(next); persist(); if (taskResult.state === "refused") setTaskResult({ state: "none" }); if (rejectedSave.current) { rejectedSave.current = false; setError(null); } }} />
    {renderTask && saveFailed && <Button variant="outline" className="justify-self-start" disabled={busy} onClick={() => void save().catch(() => undefined)}>{a3Copy.saveCurrentWriting}</Button>}
    {!renderTask && (footTarget ? createPortal(foot, footTarget) : foot)}
    {!renderTask && <Dialog open={review !== null} onOpenChange={open => { if (!open && !submitting && !unknownSubmission) closeReview(); }}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-3xl" onEscapeKeyDown={event => { if (submitting || unknownSubmission) event.preventDefault(); }} onPointerDownOutside={event => { if (submitting || unknownSubmission) event.preventDefault(); }}>
        <DialogHeader><DialogTitle>{act.sheetTitle}</DialogTitle><DialogDescription>{review ? a3Copy.due(review.terms.title, date(review.terms.due_at, a3Copy.notSet)) : a3Copy.yourWritingAsItGoes}</DialogDescription></DialogHeader>
        {review && <>
          <div className="grid gap-2"><p className="text-meta font-semibold text-muted-foreground">{a3Copy.yourWriting}</p><DocumentReadOnly document={review.draft.document} /></div>
          {!refusal && <p className="text-meta text-muted-foreground">{a3Copy.itGoesToYourInstructorExactlyAsShownYou}</p>}
          {frozen && <div role="alert" className="grid gap-3 text-ui"><p>{a3Copy.newHandInsAreUnavailableYourWritingRemainsIn}</p><div><Button variant="outline" onClick={exportWriting}>{a3Copy.downloadYourWriting}</Button></div></div>}
          {unknownSubmission && <div role="alert" className="grid gap-1"><p className="text-ui font-medium text-status-warning">{a3Copy.notConfirmedYourWorkMayOrMayNotHave}</p><p className="text-meta text-muted-foreground">{a3Copy.keepThisReviewOpenAndCheckTheSameHand}</p></div>}
          {refusal && <div role="alert" className="grid gap-1"><p className="text-ui font-medium text-status-warning">{refusal.changedAt ? a3Copy.notHandedInTheTermsChangedWhileYouWere(time(refusal.changedAt)) : a3Copy.notHandedIn2(error ?? "")}</p><p className="text-meta text-muted-foreground">{a3Copy.nothingWasHandedInReturnToYourWritingThen}</p></div>}
          <div className="flex justify-end gap-2">{refusal
            ? <Button onClick={() => { closeReview(); onRefused?.(); }}>{a3Copy.returnToWriting}</Button>
            : <><Button variant="outline" disabled={submitting || unknownSubmission} onClick={closeReview}>{a3Copy.keepWriting}</Button><Button disabled={submitting || (frozen && !unknownSubmission)} onClick={() => void submit()}>{submitting ? a3Copy.handingIn : unknownSubmission ? a3Copy.checkAgain : act.sheetTitle}</Button></>}</div>
        </>}
      </DialogContent>
    </Dialog>}
  </section>;
  return renderTask ? renderTask(task(content, { status: "ready", value: null, readAt: readAt! })) : content;
}
